import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileText,
  Loader2,
  XCircle,
  CreditCard,
  Building2,
  Calendar,
  Download,
  Eye,
} from 'lucide-react';
import ClientPageLayout from '../../components/shared/layouts/ClientPageLayout';
import { ClientEmptyState } from '../../components/client/guidance';
import Button from '../../components/shared/Button';
import InvoiceLivePreview from '../../components/vendor/invoice/InvoiceLivePreview';
import ClientPayBookingModal from '../../components/client/ClientPayBookingModal';
import {
  useAcceptClientInvoice,
  useClientInvoice,
  useClientInvoices,
  useRejectClientInvoice,
  clientInvoiceApi,
} from '../../services/clientInvoiceService';
import { useToastContext } from '../../contexts/ToastContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils';
import { downloadInvoicePdfFromInvoiceData } from '../../utils/vendorInvoicePdf';
import {
  rulesForPaymentCalculation,
  isPlatformDefaultPaymentRules,
  paymentPlanPreviewItems,
  resolveInvoicePaymentTermsDisplay,
} from '../../utils/paymentTerms';

const bookingIdFromPayPath = (payPath) => {
  if (!payPath || typeof payPath !== 'string') return null;
  const match = payPath.match(/\/bookings\/([^/?#]+)/);
  return match?.[1] || null;
};

const invoiceBookingId = (invoice) =>
  invoice?.booking_id ||
  invoice?.booking?.id ||
  bookingIdFromPayPath(invoice?.pay_path);

const isInvoiceBookingFullyPaid = (invoice) => {
  if (invoice?.is_fully_paid === true) return true;
  if (String(invoice?.status || '').toLowerCase() === 'paid') return true;
  const booking = invoice?.booking;
  if (!booking) return false;
  const subtotal = parseFloat(booking.subtotal_amount) || 0;
  const platformFee = parseFloat(booking.platform_fee) || 0;
  const amountPaid = parseFloat(booking.amount_paid) || 0;
  const totalDue = subtotal + platformFee;
  return totalDue > 0 && amountPaid + 0.009 >= totalDue;
};

const paymentPlanSummary = (invoice) => {
  const rules = invoice?.payment_schedule_rules;
  if (!rules || isPlatformDefaultPaymentRules(rules)) return null;
  const window = rulesForPaymentCalculation(rules)?.windows?.[0];
  const installments = window?.installments || [];
  if (!installments.length) return null;
  return installments
    .map((inst) => `${Math.round(inst.percentage * 100) / 100}% ${inst.label || 'payment'}`)
    .join(' · ');
};

const FILTERS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Needs decision' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Declined' },
];

const money = (n) => {
  const num = Number(n);
  if (Number.isNaN(num)) return '0.00';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatDisplayDate = (value) => {
  if (!value) return '';
  try {
    return formatDate(value);
  } catch {
    return String(value).slice(0, 10);
  }
};

const responseBadge = (invoice) => {
  if (invoice.awaits_acceptance || invoice.client_response === 'pending') {
    return (
      <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
        Awaiting your decision
      </span>
    );
  }
  if (invoice.status === 'paid' || isInvoiceBookingFullyPaid(invoice)) {
    return (
      <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
        Paid
      </span>
    );
  }
  if (invoice.client_response === 'accepted') {
    return (
      <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
        Accepted
      </span>
    );
  }
  if (invoice.client_response === 'rejected' || invoice.status === 'cancelled') {
    return (
      <span className="inline-flex rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
        Declined
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full border border-border bg-surface-muted px-2 py-0.5 text-[11px] font-medium capitalize text-ink-secondary">
      {(invoice.status || 'sent').replace('_', ' ')}
    </span>
  );
};

const vendorAddressLines = (vendor) => {
  if (!vendor) return [];
  return [
    vendor.address,
    [vendor.city, vendor.state, vendor.zip_code].filter(Boolean).join(', '),
    vendor.country,
    vendor.business_phone,
  ].filter(Boolean);
};

const InvoiceExpandedDetails = ({ listInvoice, user }) => {
  const { data: detailRes, isLoading, isError } = useClientInvoice(listInvoice.id, {
    enabled: Boolean(listInvoice.id),
  });
  const detail = detailRes?.data || listInvoice;
  const vendor = detail.vendor_detail || detail.vendor || listInvoice.vendor || {};
  const client = detail.client || {};
  const lines = Array.isArray(detail.line_items) ? detail.line_items : [];
  const previewLines = lines.map((li) => ({
    description: li.description || '',
    quantity: li.quantity ?? 1,
    unit_price: li.unit_price ?? 0,
    is_addon: Boolean(li.is_addon),
    addon_name: li.addon_name || '',
  }));

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-ink" />
      </div>
    );
  }

  if (isError && !detail?.invoice_number) {
    return (
      <p className="text-sm text-red-700 py-4">Could not load the full invoice. Try again.</p>
    );
  }

  const previewTotal = Number(detail.total_amount ?? 0);
  const planItems = paymentPlanPreviewItems(detail.payment_schedule_rules, previewTotal);

  return (
    <div className="max-w-3xl mx-auto">
      <InvoiceLivePreview
        vendorLabel={vendor.business_name || 'Vendor'}
        vendorLogoUrl={detail.logo_url || vendor.logo_url || null}
        vendorAddressLines={vendorAddressLines(vendor)}
        billToName={client.name || user?.name || 'You'}
        billToEmail={client.email || user?.email || ''}
        billToPhone={client.phone || user?.phone || ''}
        invoiceNumberPreview={detail.invoice_number || detail.id}
        invoiceDate={formatDisplayDate(detail.invoice_date)}
        dueDate={formatDisplayDate(detail.due_date)}
        lineItems={previewLines}
        taxRate={Number(detail.tax_rate ?? 0)}
        subtotal={Number(detail.subtotal ?? 0)}
        discountAmount={Number(detail.discount_amount ?? 0)}
        discountType={detail.discount_type}
        discountValue={detail.discount_value != null ? Number(detail.discount_value) : 0}
        taxAmount={Number(detail.tax_amount ?? 0)}
        total={previewTotal}
        notes={detail.notes}
        paymentTerms={resolveInvoicePaymentTermsDisplay(detail)}
        paymentPlanItems={planItems}
        bookingRef={detail.booking?.booking_reference || detail.booking_detail?.booking_reference}
        documentType={detail.document_type || 'invoice'}
        statusLabel={
          detail.status
            ? detail.status.charAt(0).toUpperCase() + detail.status.slice(1)
            : ''
        }
      />
    </div>
  );
};

const ClientInvoices = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success, error: showError } = useToastContext();
  const { confirm } = useConfirm();
  const [responseFilter, setResponseFilter] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [payBookingId, setPayBookingId] = useState(null);

  const listParams = useMemo(
    () => (responseFilter ? { response: responseFilter } : {}),
    [responseFilter]
  );
  const { data: listRes, isLoading } = useClientInvoices(listParams);
  const invoices = listRes?.data?.data || listRes?.data || [];

  const openPayForInvoice = (invoice) => {
    const bookingId = invoiceBookingId(invoice);
    if (!bookingId) {
      showError(null, 'No booking found for this invoice yet.');
      return;
    }
    setPayBookingId(bookingId);
  };

  const acceptMutation = useAcceptClientInvoice({
    onSuccess: (res) => {
      success(null, res?.message || 'Invoice accepted.');
      setBusyId(null);
      const bookingId =
        res?.data?.booking_id || bookingIdFromPayPath(res?.data?.pay_path);
      if (bookingId) setPayBookingId(bookingId);
    },
    onError: (err) => {
      setBusyId(null);
      showError(null, err?.response?.data?.message || 'Could not accept this invoice.');
    },
  });

  const rejectMutation = useRejectClientInvoice({
    onSuccess: (res) => {
      success(null, res?.message || 'Invoice declined.');
      setBusyId(null);
      setRejectingId(null);
      setRejectReason('');
    },
    onError: (err) => {
      setBusyId(null);
      showError(null, err?.response?.data?.message || 'Could not decline this invoice.');
    },
  });

  const handleAccept = async (invoice) => {
    const vendorName = invoice.vendor?.business_name || 'this vendor';
    const ok = await confirm({
      title: 'Accept this invoice?',
      message: `Accept invoice #${invoice.invoice_number} from ${vendorName}? This creates your booking so you can pay.`,
      confirmLabel: 'Accept & create order',
      cancelLabel: 'Cancel',
      variant: 'primary',
    });
    if (!ok) return;
    setBusyId(invoice.id);
    acceptMutation.mutate({ id: invoice.id });
  };

  const handleReject = (invoice) => {
    setBusyId(invoice.id);
    rejectMutation.mutate({ id: invoice.id, reason: rejectReason.trim() || null });
  };

  const toggleExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const handleDownload = async (invoice) => {
    setDownloadingId(invoice.id);
    try {
      const res = await clientInvoiceApi.getOne(invoice.id);
      const inv = res?.data || invoice;
      const vendor = inv.vendor_detail || inv.vendor || invoice.vendor || {};
      await downloadInvoicePdfFromInvoiceData(inv, vendor);
      try {
        await clientInvoiceApi.markDownloaded(invoice.id);
      } catch {
        // Non-fatal
      }
      success(null, 'Invoice PDF downloaded.');
    } catch (err) {
      showError(null, err?.response?.data?.message || 'Could not download invoice.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <>
    <ClientPageLayout
      title="Invoices"
      subtitle="Quotes from vendors after consultations. Expand any invoice to review the full details, then accept or decline."
    >
      <div className="space-y-5">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              type="button"
              onClick={() => setResponseFilter(f.value)}
              className={`product-chip ${responseFilter === f.value ? 'is-active' : ''}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-ink" />
          </div>
        ) : !invoices.length ? (
          <ClientEmptyState
            icon={FileText}
            title="No invoices yet"
            description="When a vendor sends you a quote after a consultation, it appears here so you can accept or decline."
            actionLabel="Browse vendors"
            actionTo="/browse-vendors"
          />
        ) : (
          <div className="space-y-3">
            {            invoices.map((invoice) => {
              const lines = Array.isArray(invoice.line_items) ? invoice.line_items : [];
              const isBusy = busyId === invoice.id;
              const isExpanded = expandedId === invoice.id;
              const bookingId = invoiceBookingId(invoice);
              const fullyPaid = isInvoiceBookingFullyPaid(invoice);
              const canPayBooking =
                Boolean(bookingId) &&
                (invoice.client_response === 'accepted' || Boolean(invoice.pay_path)) &&
                !fullyPaid;

              return (
                <div
                  key={invoice.id}
                  className="product-panel overflow-hidden"
                >
                  <div className="p-4 sm:p-5">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 flex-1 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-base font-semibold text-ink">
                            #{invoice.invoice_number}
                          </h3>
                          {responseBadge(invoice)}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-ink-secondary">
                          <span className="inline-flex items-center gap-1.5">
                            <Building2 className="h-3.5 w-3.5 text-ink-tertiary" />
                            {invoice.vendor?.business_name || 'Vendor'}
                          </span>
                          {invoice.due_date ? (
                            <span className="inline-flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5 text-ink-tertiary" />
                              Due {formatDate(invoice.due_date)}
                            </span>
                          ) : null}
                        </div>
                        {!isExpanded && lines.length > 0 ? (
                          <ul className="mt-2 space-y-1 text-xs text-ink-secondary">
                            {lines.slice(0, 2).map((line, idx) => (
                              <li key={idx} className="flex justify-between gap-3">
                                <span className="truncate">{line.description}</span>
                                <span className="shrink-0 tabular-nums">
                                  ${money(line.line_total ?? line.unit_price)}
                                </span>
                              </li>
                            ))}
                            {lines.length > 2 ? (
                              <li className="text-ink-tertiary">
                                +{lines.length - 2} more · open details to review
                              </li>
                            ) : null}
                          </ul>
                        ) : null}
                        {invoice.client_rejection_reason ? (
                          <p className="text-xs text-red-700">
                            Reason: {invoice.client_rejection_reason}
                          </p>
                        ) : null}
                        {(() => {
                          const plan = paymentPlanSummary(invoice);
                          return plan ? (
                            <p className="mt-1.5 text-xs text-ink-secondary">
                              <span className="font-medium text-ink">Payment plan: </span>
                              {plan}
                            </p>
                          ) : null;
                        })()}
                      </div>

                      <div className="flex shrink-0 flex-col items-stretch gap-2 sm:items-end">
                        <p className="text-right text-xl font-bold tabular-nums text-ink">
                          ${money(invoice.total_amount)}
                        </p>
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="!rounded-md"
                            icon={isExpanded ? ChevronUp : ChevronDown}
                            onClick={() => toggleExpanded(invoice.id)}
                          >
                            {isExpanded ? 'Hide details' : 'View details'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="!rounded-md"
                            icon={Download}
                            loading={downloadingId === invoice.id}
                            onClick={() => handleDownload(invoice)}
                          >
                            PDF
                          </Button>
                          {invoice.can_accept ? (
                            <Button
                              size="sm"
                              className="!rounded-md"
                              icon={CheckCircle2}
                              loading={isBusy && acceptMutation.isPending}
                              disabled={isBusy}
                              onClick={() => handleAccept(invoice)}
                            >
                              Accept &amp; create order
                            </Button>
                          ) : null}
                          {invoice.can_reject ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="!rounded-md !border-red-200 !text-red-700"
                              icon={XCircle}
                              disabled={isBusy}
                              onClick={() => {
                                setRejectingId(invoice.id);
                                setRejectReason('');
                              }}
                            >
                              Decline
                            </Button>
                          ) : null}
                          {canPayBooking ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="!rounded-md"
                              icon={CreditCard}
                              onClick={() => openPayForInvoice(invoice)}
                            >
                              Pay booking
                            </Button>
                          ) : null}
                          {bookingId ? (
                            <Button
                              size="sm"
                              variant={fullyPaid ? undefined : 'outline'}
                              className="!rounded-md"
                              icon={Eye}
                              onClick={() => navigate(`/bookings/${bookingId}`)}
                            >
                              View booking
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    </div>

                    {rejectingId === invoice.id ? (
                      <div className="mt-4 space-y-2 border-t border-border pt-4">
                        <label className="block text-xs font-medium text-ink">
                          Optional reason for the vendor
                        </label>
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          rows={2}
                          className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-ink"
                          placeholder="e.g. Budget doesn’t fit / chose another package"
                        />
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="!rounded-md"
                            onClick={() => setRejectingId(null)}
                            disabled={isBusy}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            className="!rounded-md !bg-red-600 hover:!bg-red-700"
                            loading={isBusy && rejectMutation.isPending}
                            onClick={() => handleReject(invoice)}
                          >
                            Confirm decline
                          </Button>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {isExpanded ? (
                    <div className="border-t border-border bg-canvas px-3 py-5 sm:px-6 sm:py-6">
                      <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">
                        Invoice details
                      </p>
                      <InvoiceExpandedDetails listInvoice={invoice} user={user} />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </ClientPageLayout>

    <ClientPayBookingModal
      bookingId={payBookingId}
      isOpen={Boolean(payBookingId)}
      onClose={() => setPayBookingId(null)}
    />
    </>
  );
};

export default ClientInvoices;
